import { config } from "@/config";
import type { GetUserDetailResponse, OtherUser } from "@/interfaces/user";
import { Provider } from "@/providers/http.provider";
import { objectToQueryString } from "@/util/transform";
import type { IOrganizationObject } from "@/interfaces/organization";

class AuthProvider extends Provider {
  constructor() {
    super({
      baseURL: `${config.baseAPIUrl}/rest/access/`,
    });
  }

  private isVercelPreviewUrl(url: string): boolean {
    try {
      const hostname = new URL(url).hostname;
      return hostname.startsWith("rebuild-") && hostname.endsWith(".vercel.app");
    } catch {
      return false;
    }
  }

  async login(email: string): Promise<{ error: string; success: boolean }> {
    const isPreviewUrl = this.isVercelPreviewUrl(window.location.origin);

    const params: Record<string, string> = {
      origin: isPreviewUrl ? config.vercelPreviewAuthDomain : window.location.origin,
      email,
    };

    if (isPreviewUrl) {
      params.redirectTo = window.location.origin;
    }

    const query = objectToQueryString(params);

    return super.get<{ error: string; success: boolean }>(`/sendSignInLink?${query}`);
  }

  async getUserDetails(): Promise<GetUserDetailResponse> {
    return super.post<GetUserDetailResponse>(`/getUserDetails`, {
      extraData: {
        fetchCarrierList: true,
        fetchOfficeList: true,
        fetchOtherUserList: true,
        fetchCustomSettings: true,
      },
    });
  }

  /**
   * Company admin roster: everyone on the allow list (`allowed_users`), including invites
   * who have not logged in yet. Server requires admin (or creator/internal).
   */
  async getAdminAllowedUsersInOrg(): Promise<GetUserDetailResponse> {
    return super.post<GetUserDetailResponse>(`/getUserDetails`, {
      extraData: {
        fetchAllowedUsersInOrg: true,
      },
    });
  }

  async editOrganization(
    organization: {
      organizationId: string;
      organizationName?: string;
      recipientsXNAddress?: string;
      customSettings?: string;
    },
    options?: {
      users?: Array<{
        email: string;
        firstName: string;
        lastName: string;
        role: string;
        photoURL?: string;
        assignedOffices?: string[];
      }>;
      allowedDomain?: string;
    },
  ): Promise<{ success: boolean; error?: string }> {
    return super.post<{ success: boolean; error?: string }>(`/editOrganization`, {
      organization,
      ...(options?.users && options.users.length > 0 ? { users: options.users } : {}),
      ...(options?.allowedDomain !== undefined ? { allowedDomain: options.allowedDomain } : {}),
    });
  }

  async listOrganizations(): Promise<{
    success: boolean;
    data: { organizations: IOrganizationObject[] };
    error?: string;
  }> {
    return super.get<{
      success: boolean;
      data: { organizations: IOrganizationObject[] };
      error?: string;
    }>(`/listOrganizations`);
  }

  async registerNewUser(user: OtherUser): Promise<{ success: boolean; data: OtherUser; error?: string }> {
    return super.post<{ success: boolean; data: OtherUser; error?: string }>(`/registerNewUser`, {
      newUser: user,
    });
  }

  async checkCode(code: string, email: string): Promise<{ success: boolean; data?: string; error?: string; retryAfter?: number }> {
    const params = {
      code,
      email,
    };
    const query = objectToQueryString(params);
    return super.get<{ success: boolean; data?: string; error?: string; retryAfter?: number }>(`/checkCode?${query}`);
  }

  async logout(): Promise<{ success: boolean; error?: string; message?: string }> {
    return super.post<{ success: boolean; error?: string; message?: string }>(`/logout`);
  }

  async updateUserDetails(data: {
    firstName: string;
    lastName: string;
    organizationId: string;
    lassoUserId: string;
    email: string;
    role: string;
    photoURL?: string;
    assignedOffices?: string[];
  }): Promise<{
    success: boolean;
    error?: string;
  }> {
    return super.post<{ success: boolean; error?: string }>(`/editUser`, data);
  }

  async deleteOrganizationUser(organizationId: string, userId: string): Promise<{ success: boolean; error?: string }> {
    return super.delete<{ success: boolean; error?: string }>(
      `/organization/${encodeURIComponent(organizationId)}/user/${encodeURIComponent(userId)}`,
    );
  }
}

export default new AuthProvider();
